import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// ── Colour palette ──
const C = {
  bg: '#0f1117', card: '#1a1d27', cardBorder: '#2a2d3a',
  purple: '#7c3aed', purpleLight: '#a78bfa', purpleDark: '#5b21b6',
  blue: '#38bdf8', cyan: '#22d3ee', green: '#4ade80', yellow: '#facc15',
  orange: '#fb923c', pink: '#f472b6', red: '#ef4444',
  text: '#e2e8f0', textMuted: '#94a3b8', textDim: '#64748b',
  confirmed: '#38bdf8', provisional: '#4ade80', quote: '#fb923c',
  unweighted: '#facc15', budget: '#22d3ee', adjBudget: '#f59e0b',
};

const PERSON_COLORS = [
  '#38bdf8', '#4ade80', '#fb923c', '#facc15', '#f472b6',
  '#a78bfa', '#22d3ee', '#ef4444', '#34d399', '#c084fc',
  '#fbbf24', '#6ee7b7',
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FULL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmt = (v) => {
  if (v == null) return '';
  const abs = Math.abs(v);
  if (abs >= 1e6) return `£${(v / 1e6).toFixed(2)}m`;
  if (abs >= 1e3) return `£${(v / 1e3).toFixed(0)}k`;
  return `£${v.toFixed(0)}`;
};

const fmtFull = (v) => {
  if (v == null) return '';
  return `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const pct = (v) => {
  if (v == null) return '';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

const DashTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 8, padding: '10px 14px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, color: C.text }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: p.color }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>{fmtFull(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const KPI = ({ label, value, sub, color, trend }) => (
  <div className="kpi-card">
    <div className="kpi-label">{label}</div>
    <div className="kpi-value" style={{ color: color || C.text }}>{value}</div>
    {sub && <div className="kpi-sub">{trend === 'up' ? '▲ ' : trend === 'down' ? '▼ ' : ''}{sub}</div>}
  </div>
);

const Loading = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: C.textDim }}>
    <div className="spinner" />
    <p style={{ marginTop: 16, fontSize: 13 }}>Loading dashboard data...</p>
  </div>
);

const PipelineChangeChart = ({ year, onBarClick }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [chartData, setChartData] = useState([]);

  const fmtDate = (d) => d ? d.toISOString().split('T')[0] : '';

  useEffect(() => {
    let url = `/api/query/pipeline-change?year=${year}`;
    if (fromDate && toDate) {
      url += `&from=${fmtDate(fromDate)}&to=${fmtDate(toDate)}`;
    }
    fetch(url)
      .then(res => res.ok ? res.json() : [])
      .then(rows => setChartData(rows.map(r => ({ month: r.RevenueMonth, change: r.TotalChange || 0 }))))
      .catch(() => setChartData([]));
  }, [year, fromDate, toDate]);

  const handleClick = (data) => {
    if (onBarClick && data && data.activeLabel) onBarClick(data.activeLabel);
  };

  return (
    <div className="chart-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 className="chart-title" style={{ margin: 0 }}>Weighted Sale Pipeline Change <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>· click to drill</span></h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <DatePicker selected={fromDate} onChange={setFromDate} placeholderText="From"
            dateFormat="dd/MM/yyyy" className="date-input" isClearable />
          <span style={{ color: C.textDim }}>to</span>
          <DatePicker selected={toDate} onChange={setToDate} placeholderText="To"
            dateFormat="dd/MM/yyyy" className="date-input" isClearable minDate={fromDate} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} barSize={28} onClick={handleClick} style={{ cursor: 'pointer' }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
          <XAxis dataKey="month" tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={v => v.slice(0, 3)} />
          <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={fmt} />
          <Tooltip content={<DashTooltip />} />
          <Bar dataKey="change" fill={C.purpleLight} name="Pipeline Change" radius={[3, 3, 0, 0]} style={{ cursor: 'pointer' }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const MultiSelect = ({ label, options, selected, setSelected, formatLabel }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getLabel = (o) => formatLabel ? formatLabel(o) : o;

  return (
    <div className="multi-select" ref={ref}>
      <div className="multi-select-btn" onClick={() => setOpen(!open)}>
        {selected.length === 0 ? label : `${label} (${selected.length})`}
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="multi-select-dropdown">
          <div className="multi-select-option" onClick={() => setSelected([])}>
            <input type="checkbox" checked={selected.length === 0} readOnly /> All
          </div>
          {options.map(o => (
            <div key={o} className="multi-select-option" onClick={() => {
              setSelected(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o]);
            }}>
              <input type="checkbox" checked={selected.includes(o)} readOnly /> {getLabel(o)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobDrillDown = ({ year, person, drillMonth, drillMonths, drillJobType, drillType, drillValue, onClearDrill, drillRef }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('DueOut');
  const [sortDir, setSortDir] = useState('desc');
  const [filterMonths, setFilterMonths] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);

  useEffect(() => {
    const personParam = person ? `&person=${encodeURIComponent(person)}` : '';
    fetch(`/api/query/job-details?year=${year}${personParam}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => { setJobs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, person]);

  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const MONTH_ABBRS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const getMonthName = (val) => {
    if (!val) return val;
    const num = parseInt(val);
    if (!isNaN(num) && num >= 1 && num <= 12) return MONTH_NAMES[num - 1];
    if (typeof val === 'string' && val.includes(' - ')) return val.split(' - ')[1];
    return val;
  };

  // Get the monthly revenue columns for a given month name
  const getMonthlyRevenue = (job, fullMonthName) => {
    const idx = MONTH_NAMES.indexOf(fullMonthName);
    if (idx < 0) return null;
    const abbr = MONTH_ABBRS[idx];
    return {
      conf: Number(job[`Conf_${abbr}`]) || 0,
      prov: Number(job[`Prov_${abbr}`]) || 0,
      quote: Number(job[`Quote_${abbr}`]) || 0,
    };
  };

  // Check if a job has any revenue in the given month
  const jobHasRevenueInMonth = (job, fullMonthName) => {
    const rev = getMonthlyRevenue(job, fullMonthName);
    if (!rev) return false;
    return rev.conf !== 0 || rev.prov !== 0 || rev.quote !== 0;
  };

  const allMonths = [...new Set(jobs.map(j => j.DueOutMonth).filter(Boolean))].sort((a, b) => {
    const na = parseInt(a) || 0, nb = parseInt(b) || 0;
    return na - nb;
  });
  const allStatuses = [...new Set(jobs.map(j => j.Status).filter(Boolean))].sort();
  const allTypes = [...new Set(jobs.map(j => j.JobType).filter(Boolean))].sort();

  const filtered = jobs.filter(j => {
    if (search) {
      const s = search.toLowerCase();
      if (!(j.JobRef || '').toLowerCase().includes(s)
        && !(j.JobTitle || '').toLowerCase().includes(s)
        && !(j.Client || '').toLowerCase().includes(s)
        && !(j.Status || '').toLowerCase().includes(s)
        && !(j.JobType || '').toLowerCase().includes(s)) return false;
    }
    // Apply drill-down month filter — matches jobs with revenue in that month
    if (drillMonth && !jobHasRevenueInMonth(j, drillMonth)) return false;
    // Apply drill-down quarter filter — matches jobs with revenue in any of those months
    if (drillMonths && !drillMonths.some(m => jobHasRevenueInMonth(j, m))) return false;
    // Apply drill-down job type filter
    if (drillJobType && (j.JobType || '') !== drillJobType) return false;
    if (filterMonths.length && !filterMonths.includes(j.DueOutMonth)) return false;
    if (filterStatuses.length && !filterStatuses.includes(j.Status)) return false;
    if (filterTypes.length && !filterTypes.includes(j.JobType)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortCol], bv = b[sortCol];
    if (sortCol.includes('Income') || sortCol.includes('Value')) {
      av = Number(av) || 0; bv = Number(bv) || 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    av = (av || '').toString(); bv = (bv || '').toString();
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  // Get revenue values — monthly portion when drilled by month/quarter, totals otherwise
  const getRevenue = (j) => {
    if (drillMonth) {
      const m = getMonthlyRevenue(j, drillMonth);
      return { quote: m?.quote || 0, prov: m?.prov || 0, conf: m?.conf || 0, inv: null };
    }
    if (drillMonths) {
      let quote = 0, prov = 0, conf = 0;
      drillMonths.forEach(mo => {
        const m = getMonthlyRevenue(j, mo);
        if (m) { quote += m.quote; prov += m.prov; conf += m.conf; }
      });
      return { quote, prov, conf, inv: null };
    }
    return {
      quote: Number(j.QuoteIncome) || 0,
      prov: Number(j.ProvisionalIncome) || 0,
      conf: Number(j.ConfirmedIncome) || 0,
      inv: Number(j.InvoicedValue) || 0,
    };
  };

  const isMonthDrill = drillMonth || drillMonths;

  const totals = sorted.reduce((acc, j) => {
    const r = getRevenue(j);
    return {
      quote: acc.quote + r.quote,
      prov: acc.prov + r.prov,
      conf: acc.conf + r.conf,
      inv: r.inv != null ? acc.inv + r.inv : acc.inv,
    };
  }, { quote: 0, prov: 0, conf: 0, inv: 0 });

  if (loading) return <Loading />;

  const display = sorted.slice(0, 200);
  const drillLabel = drillValue || '';
  const monthLabel = isMonthDrill ? ` (${drillLabel})` : '';

  // Build banner text based on drill type
  const bannerText = drillType === 'month' ? <>Showing jobs with revenue in <strong>{drillValue}</strong> — values show this month's portion</>
    : drillType === 'quarter' ? <>Showing jobs with revenue in <strong>{drillValue}</strong> — values show quarter portion</>
    : drillType === 'jobType' ? <>Showing <strong>{drillValue}</strong> jobs</>
    : <>Filtered by <strong>{drillValue}</strong></>;

  return (
    <div className="chart-card" style={{ marginTop: 16 }} ref={drillRef}>
      <h3 className="chart-title">Job Details</h3>
      {drillType && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', marginBottom: 8,
          background: 'rgba(124, 58, 237, 0.15)', border: `1px solid ${C.purple}`, borderRadius: 8, fontSize: 13
        }}>
          <span style={{ color: C.purpleLight }}>📊 {bannerText}</span>
          <button onClick={onClearDrill} style={{
            marginLeft: 'auto', background: 'rgba(255,255,255,0.1)', border: 'none', color: C.text,
            borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 12
          }}>✕ Clear</button>
        </div>
      )}
      <div className="drilldown-filters" style={{ gap: 12, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search jobs..." value={search}
          onChange={e => setSearch(e.target.value)} className="date-input" style={{ width: 200 }} />
        <MultiSelect label="Month Out" options={allMonths} selected={filterMonths} setSelected={setFilterMonths}
          formatLabel={getMonthName} />
        <MultiSelect label="Status" options={allStatuses} selected={filterStatuses} setSelected={setFilterStatuses} />
        <MultiSelect label="Job Type" options={allTypes} selected={filterTypes} setSelected={setFilterTypes} />
        <span style={{ color: C.textDim, fontSize: 12 }}>{sorted.length} jobs</span>
      </div>
      <div className="drilldown-table-wrap">
        <table className="drilldown-table">
          <thead>
            <tr>
              {[
                { key: 'JobRef', label: 'Job Ref' },
                { key: 'JobTitle', label: 'Job Title' },
                { key: 'Client', label: 'Client' },
                { key: 'JobType', label: 'Job Type' },
                { key: 'DueOut', label: 'Due Out' },
                { key: 'DueBack', label: 'Due Back' },
                { key: 'DueOutMonth', label: 'Due Out Month' },
                { key: 'Status', label: 'Status' },
                { key: 'QuoteIncome', label: `Quote${monthLabel}` },
                { key: 'ProvisionalIncome', label: `Provisional${monthLabel}` },
                { key: 'ConfirmedIncome', label: `Confirmed${monthLabel}` },
                ...(!isMonthDrill ? [{ key: 'InvoicedValue', label: 'Invoiced' }] : []),
              ].map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: 'pointer' }}>
                  {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {display.map((j, i) => {
              const r = getRevenue(j);
              return (
                <tr key={i}>
                  <td>{j.JobRef}</td><td>{j.JobTitle}</td><td>{j.Client}</td><td>{j.JobType}</td>
                  <td>{j.DueOut}</td><td>{j.DueBack}</td><td>{getMonthName(j.DueOutMonth)}</td><td>{j.Status}</td>
                  <td style={{ textAlign: 'right' }}>{fmtFull(r.quote)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtFull(r.prov)}</td>
                  <td style={{ textAlign: 'right' }}>{fmtFull(r.conf)}</td>
                  {!isMonthDrill && <td style={{ textAlign: 'right' }}>{fmtFull(r.inv)}</td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td colSpan={8} style={{ textAlign: 'right', fontWeight: 600 }}>Totals:</td>
              <td style={{ textAlign: 'right' }}>{fmtFull(totals.quote)}</td>
              <td style={{ textAlign: 'right' }}>{fmtFull(totals.prov)}</td>
              <td style={{ textAlign: 'right' }}>{fmtFull(totals.conf)}</td>
              {!isMonthDrill && <td style={{ textAlign: 'right' }}>{fmtFull(totals.inv)}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
      {sorted.length > 200 && (
        <div style={{ textAlign: 'center', padding: 8, color: C.textDim, fontSize: 12 }}>
          Showing 200 of {sorted.length} — use filters to narrow down
        </div>
      )}
    </div>
  );
};

const YoYChart = ({ year, person, onBarClick }) => {
  const [data, setData] = useState([]);
  const [years, setYears] = useState([]);
  const [fromMonth, setFromMonth] = useState(0);
  const [toMonth, setToMonth] = useState(11);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const personParam = person ? `&person=${encodeURIComponent(person)}` : '';
    const url = `/api/query/yoy-comparison?year=${year}&fromMonth=${fromMonth + 1}&toMonth=${toMonth + 1}${personParam}`;
    fetch(url)
      .then(res => res.ok ? res.json() : [])
      .then(rows => {
        // Build { JobType: 'Corporate', 2025: 500000, 2026: 700000 } per job type
        const jtMap = {};
        const yrs = new Set();
        rows.forEach(r => {
          const jt = r.JobType || 'Other';
          const y = r.RevenueYear;
          yrs.add(y);
          if (!jtMap[jt]) jtMap[jt] = { JobType: jt };
          jtMap[jt][y] = (jtMap[jt][y] || 0) + (r.TotalConfirmed || 0);
        });
        setYears([...yrs].sort());
        setData(Object.values(jtMap).sort((a, b) => a.JobType.localeCompare(b.JobType)));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [year, fromMonth, toMonth, person]);

  const rangeLabel = fromMonth === 0 && toMonth === 11 ? 'Full Year'
    : `${MONTHS[fromMonth]} – ${MONTHS[toMonth]}`;

  if (loading) return null;

  return (
    <div className="chart-card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 className="chart-title" style={{ margin: 0 }}>Confirmed Revenue vs {year - 1} ({rangeLabel}) <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>· click to drill</span></h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="date-input" value={fromMonth} onChange={e => setFromMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <span style={{ color: C.textDim, fontSize: 12 }}>to</span>
          <select className="date-input" value={toMonth} onChange={e => setToMonth(Number(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={data} barCategoryGap="15%" onClick={(d) => {
          if (onBarClick && d && d.activeLabel) onBarClick(d.activeLabel);
        }} style={{ cursor: 'pointer' }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
          <XAxis dataKey="JobType" tick={{ fill: C.textMuted, fontSize: 10, angle: -35, textAnchor: 'end' }} height={80} interval={0} />
          <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={fmt} />
          <Tooltip content={<DashTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, color: C.textMuted }} />
          {years.map((yr, i) => (
            <Bar key={yr} dataKey={yr} name={String(yr)}
              fill={i === 0 ? C.confirmed : C.orange}
              radius={[3, 3, 0, 0]} style={{ cursor: 'pointer' }} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ══════════════════════════════════════════════
// MAIN SALES DASHBOARD COMPONENT
// ══════════════════════════════════════════════

export default function SalesDashboard({ user }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(2026);
  const [person, setPerson] = useState('');
  const [people, setPeople] = useState([]);
  const [autoMatched, setAutoMatched] = useState(false);
  const [drillMonth, setDrillMonth] = useState(null);
  const [drillType, setDrillType] = useState(null); // 'month' | 'quarter' | 'jobType' | 'person'
  const [drillValue, setDrillValue] = useState(null); // display label
  const [drillMonths, setDrillMonths] = useState(null); // array of month names for quarter drill
  const [drillJobType, setDrillJobType] = useState(null);
  const jobsRef = useRef(null);

  const scrollToJobs = () => {
    setTimeout(() => { jobsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
  };

  const clearDrill = () => {
    setDrillMonth(null); setDrillType(null); setDrillValue(null);
    setDrillMonths(null); setDrillJobType(null);
  };

  // Sales By Month chart click → drill by single month
  const handleBarClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const month = data.activeLabel;
      clearDrill();
      setDrillMonth(month); setDrillType('month'); setDrillValue(month);
      scrollToJobs();
    }
  };

  // Quarterly table click → drill by 3 months
  const handleQuarterClick = (quarterIdx) => {
    const qMonths = FULL_MONTHS.slice(quarterIdx * 3, quarterIdx * 3 + 3);
    clearDrill();
    setDrillMonths(qMonths); setDrillType('quarter'); setDrillValue(`Q${quarterIdx + 1}`);
    scrollToJobs();
  };

  // Confirmed by Person chart click → drill by month
  const handleConfByPersonClick = (data) => {
    if (data && data.activePayload && data.activePayload.length > 0) {
      const month = data.activeLabel;
      clearDrill();
      setDrillMonth(month); setDrillType('month'); setDrillValue(month);
      scrollToJobs();
    }
  };

  // YoY chart click → drill by job type
  const handleYoYClick = (jobType) => {
    clearDrill();
    setDrillJobType(jobType); setDrillType('jobType'); setDrillValue(jobType);
    scrollToJobs();
  };

  // Pipeline Change chart click → drill by month
  const handlePipelineClick = (month) => {
    clearDrill();
    setDrillMonth(month); setDrillType('month'); setDrillValue(month);
    scrollToJobs();
  };

  const fetchPeople = useCallback(() => {
    if (!user) return;
    fetch(`/api/query/salesperson-list?year=${year}`)
      .then(res => res.ok ? res.json() : [])
      .then(rows => {
        const names = rows.map(r => r.Sales_Person).filter(Boolean);
        setPeople(names);
        // Auto-select: use LinkedSalesPerson first, then match by display name
        if (!autoMatched && names.length) {
          const linked = user?.salesPerson;
          if (linked && names.includes(linked)) {
            setPerson(linked); setAutoMatched(true);
          } else if (user?.name) {
            const match = names.find(n => n.toLowerCase() === user.name.toLowerCase());
            if (match) { setPerson(match); setAutoMatched(true); }
          }
        }
      })
      .catch(() => setPeople([]));
  }, [user, year, autoMatched]);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const personParam = person ? `&person=${encodeURIComponent(person)}` : '';

      const salesRes = await fetch(`/api/query/sales-by-month?year=${year}${personParam}`);
      if (salesRes.ok) {
        const rows = await salesRes.json();
        const monthMap = {};
        FULL_MONTHS.forEach(m => {
          monthMap[m] = { month: m, conf: 0, prov75: 0, quote10: 0, unweighted: 0, budget: 0, adjBudget: 0 };
        });
        rows.forEach(r => {
          const entry = monthMap[r.RevenueMonth];
          if (!entry) return;
          const amt = r.RevenueAmount || 0;
          switch (r.RevenueType) {
            case '01. Confirmed': entry.conf = amt; break;
            case '02. Provisional @75%': entry.prov75 = amt; break;
            case '03. Quote @10%': entry.quote10 = amt; break;
            case '04. Unweighted Pipeline': entry.unweighted = amt; break;
            case '06. Budget': entry.budget = amt; break;
            case '07. Adj budget': entry.adjBudget = amt; break;
            default: break;
          }
        });
        setData(prev => ({ ...prev, salesByMonth: FULL_MONTHS.map(m => monthMap[m]) }));
      }

      const confRes = await fetch(`/api/query/confirmed-by-person?year=${year}${personParam}`);
      if (confRes.ok) {
        const rows = await confRes.json();
        setData(prev => ({ ...prev, confirmedByPerson: rows }));
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user, year, person]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthly = data.salesByMonth || [];
  const totalConf = monthly.reduce((s, d) => s + d.conf, 0);
  const totalProv = monthly.reduce((s, d) => s + d.prov75, 0);
  const totalQuote = monthly.reduce((s, d) => s + d.quote10, 0);
  const totalAdjBudget = monthly.reduce((s, d) => s + d.adjBudget, 0);
  const totalWeighted = totalConf + totalProv + totalQuote;

  const quarterData = [0, 1, 2, 3].map(q => {
    const bud = monthly.slice(q * 3, q * 3 + 3).reduce((s, d) => s + d.adjBudget, 0);
    const act = monthly.slice(q * 3, q * 3 + 3).reduce((s, d) => s + d.conf, 0);
    const variance = act - bud;
    return { quarter: `Q${q + 1}`, budget: bud, actual: act, variance, variancePct: bud ? (variance / bud) * 100 : 0 };
  });

  const confByPersonRows = data.confirmedByPerson || [];
  const confByPersonNames = [...new Set(confByPersonRows.map(r => r.Sales_Person).filter(Boolean))];
  const confByPersonData = FULL_MONTHS.map(m => {
    const entry = { month: m };
    confByPersonNames.forEach(p => { entry[p] = 0; });
    confByPersonRows.filter(r => r.RevenueMonth === m).forEach(r => {
      if (r.Sales_Person) entry[r.Sales_Person] = r.RevenueAmount || 0;
    });
    return entry;
  });

  return (
    <div className="crm-content" style={{ padding: '16px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Sales Forecast</h2>
          <p style={{ color: C.textDim, fontSize: 13, marginTop: 4 }}>Revenue tracking and pipeline analysis</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="crm-filter-select" value={person} onChange={e => setPerson(e.target.value)}>
            <option value="">All Sales People</option>
            {people.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select className="crm-filter-select" value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2023, 2024, 2025, 2026, 2027, 2028, 2029].map(y => (
              <option key={y} value={y}>FY{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? <Loading /> : (
        <>
          <div className="kpi-row">
            <KPI label="Confirmed Revenue" value={fmt(totalConf)} color={C.confirmed}
              sub={totalAdjBudget ? `${((totalConf / totalAdjBudget) * 100).toFixed(1)}% of adj budget` : ''}
              trend={totalConf > totalAdjBudget * 0.3 ? 'up' : 'down'} />
            <KPI label="Weighted Pipeline" value={fmt(totalWeighted)} color={C.green}
              sub={`Prov: ${fmt(totalProv)} + Quote: ${fmt(totalQuote)}`} />
            <KPI label="Full Year Adj Budget" value={fmt(totalAdjBudget)} color={C.budget} />
            <KPI label="Variance" value={fmt(totalConf - totalAdjBudget)}
              color={totalConf >= totalAdjBudget ? C.green : C.red}
              sub={totalAdjBudget ? pct(((totalConf - totalAdjBudget) / totalAdjBudget) * 100) : ''}
              trend={totalConf >= totalAdjBudget ? 'up' : 'down'} />
          </div>

          <div className="chart-grid">
            <div className="chart-card">
              <h3 className="chart-title">Sales By Month vs Budget <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>· click a bar to drill down</span></h3>
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={monthly} barSize={32} onClick={handleBarClick} style={{ cursor: 'pointer' }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis dataKey="month" tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={v => v.slice(0, 3)} />
                  <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={fmt} />
                  <Tooltip content={<DashTooltip />} />
                  <Bar dataKey="conf" stackId="a" fill={C.confirmed} name="Confirmed" style={{ cursor: 'pointer' }} />
                  <Bar dataKey="prov75" stackId="a" fill={C.provisional} name="Provisional @75%" style={{ cursor: 'pointer' }} />
                  <Bar dataKey="quote10" stackId="a" fill={C.quote} name="Quote @10%" style={{ cursor: 'pointer' }} />
                  <Bar dataKey="unweighted" stackId="a" fill={C.unweighted} name="Unweighted" radius={[3, 3, 0, 0]} style={{ cursor: 'pointer' }} />
                  <Line type="monotone" dataKey="budget" stroke={C.cyan} strokeWidth={2} dot={false} name="Budget" />
                  <Line type="monotone" dataKey="adjBudget" stroke={C.adjBudget} strokeWidth={2} dot={false} name="Adj Budget" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-card">
              <h3 className="chart-title">Full Year Sales vs Budget</h3>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={[
                  { name: 'Actual', confirmed: totalConf, prov75: totalProv, quote10: totalQuote, unweighted: monthly.reduce((s, d) => s + d.unweighted, 0), adjBudget: 0 },
                  { name: 'Adj Budget', confirmed: 0, prov75: 0, quote10: 0, unweighted: 0, adjBudget: totalAdjBudget },
                ]} barSize={80}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                  <XAxis dataKey="name" tick={{ fill: C.textMuted, fontSize: 13 }} />
                  <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={fmt} />
                  <Tooltip content={<DashTooltip />} />
                  <Bar dataKey="confirmed" stackId="a" fill={C.confirmed} name="Confirmed" />
                  <Bar dataKey="prov75" stackId="a" fill={C.provisional} name="Provisional @75%" />
                  <Bar dataKey="quote10" stackId="a" fill={C.quote} name="Quote @10%" />
                  <Bar dataKey="unweighted" stackId="a" fill={C.unweighted} name="Unweighted" />
                  <Bar dataKey="adjBudget" stackId="a" fill={C.adjBudget} name="Adj Budget" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="chart-grid" style={{ marginTop: 16 }}>
            {person ? (
              <div className="chart-card chart-disabled">
                <h3 className="chart-title" style={{ margin: 0 }}>Weighted Sale Pipeline Change</h3>
                <div className="chart-disabled-msg">Select "All Sales People" to view this chart</div>
              </div>
            ) : (
              <PipelineChangeChart year={year} onBarClick={handlePipelineClick} />
            )}
            <div className="chart-card">
              <h3 className="chart-title">Quarterly Sales Summary (Confirmed Only) <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>· click to drill</span></h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Quarter</th>
                    <th>Budget</th>
                    <th>Actual</th>
                    <th>Variance</th>
                    <th>Var %</th>
                  </tr>
                </thead>
                <tbody>
                  {quarterData.map((q, i) => (
                    <tr key={i} onClick={() => handleQuarterClick(i)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ textAlign: 'left', fontWeight: 500 }}>{q.quarter}</td>
                      <td>{fmtFull(q.budget)}</td>
                      <td style={{ color: C.text }}>{fmtFull(q.actual)}</td>
                      <td style={{ color: q.variance >= 0 ? C.green : C.red, fontWeight: 500 }}>{fmtFull(q.variance)}</td>
                      <td style={{ color: q.variancePct >= 0 ? C.green : C.red, fontWeight: 600 }}>{pct(q.variancePct)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td style={{ textAlign: 'left' }}>Grand Total</td>
                    <td>{fmtFull(totalAdjBudget)}</td>
                    <td style={{ color: C.text }}>{fmtFull(totalConf)}</td>
                    <td style={{ color: totalConf - totalAdjBudget >= 0 ? C.green : C.red }}>{fmtFull(totalConf - totalAdjBudget)}</td>
                    <td style={{ color: totalAdjBudget && ((totalConf - totalAdjBudget) / totalAdjBudget * 100) >= 0 ? C.green : C.red }}>
                      {totalAdjBudget ? pct((totalConf - totalAdjBudget) / totalAdjBudget * 100) : '0.00%'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="chart-card" style={{ marginTop: 16 }}>
            <h3 className="chart-title">Confirmed Revenue by Person <span style={{ fontSize: 11, color: C.textDim, fontWeight: 400 }}>· click to drill</span></h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={confByPersonData} barCategoryGap="10%" onClick={handleConfByPersonClick} style={{ cursor: 'pointer' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                <XAxis dataKey="month" tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={v => v.slice(0, 3)} />
                <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} tickFormatter={fmt} />
                <Tooltip content={<DashTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: C.textMuted }} />
                {confByPersonNames.map((name, i) => (
                  <Bar key={name} dataKey={name} stackId="a" fill={PERSON_COLORS[i % PERSON_COLORS.length]}
                    radius={i === confByPersonNames.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} style={{ cursor: 'pointer' }} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <YoYChart year={year} person={person} onBarClick={handleYoYClick} />
          <JobDrillDown year={year} person={person} drillMonth={drillMonth} drillMonths={drillMonths}
            drillJobType={drillJobType} drillType={drillType} drillValue={drillValue}
            onClearDrill={clearDrill} drillRef={jobsRef} />
        </>
      )}
      <div className="dashboard-footer">
        Data sourced from HireTrack NX — updated overnight daily
      </div>
    </div>
  );
}
